import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type SalaryView = 'personal' | 'team' | 'department' | 'company' | 'recruiter';

export interface SalaryBand {
  id: string;
  identifier: string;
  office: string;
  department: string;
  unit: string;
  cluster: string;
  careerPath: string;
  title: string;
  level: number;
  degree: string;
  min: number;
  average: number;
  max: number;
  oteMin: number;
  oteAverage: number;
  oteMax: number;
  lowerThird: string;
  middleThird: string;
  upperThird: string;
  grantValueVsop: number;
  vsop?: number;
}

export interface SalaryContext {
  employee: null | {
    fullName: string;
    level: number;
    department: string;
    team: string;
    remoteOutsideGermany: boolean;
  };
  views: Record<SalaryView, boolean>;
  fields: { vsopShares: boolean; companyRating: boolean };
  department: null | { scope: string; levelExclusive: number };
}

export interface SalaryBandMember {
  id: string;
  fullName: string;
  careerPath: string;
  identifier: string;
  level: number;
  office: string;
  min: number;
  average: number;
  max: number;
  grantValueVsop: number;
  hasSalaryBand: boolean;
}

export type TeamMemberBand = SalaryBandMember;

async function getJson<T>(path: string): Promise<T> {
  const response = await authenticatedFetch(`${PROXY_URL}/api/salary-bands${path}`);
  return response.json() as Promise<T>;
}

export function fetchSalaryContext() {
  return getJson<SalaryContext>('/context');
}

export function fetchPersonalSalaryBands() {
  return getJson<{
    current: SalaryBand[];
    next: SalaryBand[];
    careerPath: SalaryBand[];
    remoteOutsideGermany: boolean;
  }>('/personal');
}

export function fetchTeamSalaryBands() {
  return getJson<{ reports: TeamMemberBand[]; levelInclusive: number }>('/team');
}

export function fetchTeamMemberBands(employeeId: string) {
  return getJson<{
    employee: { fullName: string; careerPath: string };
    bands: SalaryBand[];
  }>(`/team/${encodeURIComponent(employeeId)}/bands`);
}

export function fetchDepartmentSalaryBands() {
  return getJson<{
    scope: string;
    levelExclusive: number;
    bands: SalaryBand[];
    members: SalaryBandMember[];
  }>('/department');
}

export function fetchDepartmentMemberBands(employeeId: string) {
  return getJson<{ employee: SalaryBandMember; bands: SalaryBand[] }>(
    `/department/${encodeURIComponent(employeeId)}/bands`,
  );
}

export function fetchCompanySalaryBands() {
  return getJson<{ bands: SalaryBand[]; canViewCompanyRating: boolean }>('/company');
}

export function fetchRecruiterSalaryBands() {
  return getJson<{ bands: SalaryBand[] }>('/recruiter');
}
